import { FormControl, FormControlProps, SelectChangeEvent } from "@mui/material";
import type { JSX } from "react";
import { Controller } from "react-hook-form";
import type { Control, FieldPath, FieldValues, PathValue } from "react-hook-form";

import { SelectWithError, SelectWithErrorProps } from "@/components/inputsWithError";

export interface FormControlSelectProps<
  T extends string | number | "" = string,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TContext = unknown,
> extends SelectWithErrorProps<T> {
  name: TName;
  control: Control<TFieldValues, TContext>;
  errorMsg?: string;
  mapChangedValue?: (value: T) => PathValue<TFieldValues, TName>;
  mapValue?: (value: PathValue<TFieldValues, TName>) => T | null;
  sx?: FormControlProps["sx"];
  options: { value: T; label: string; disabled?: boolean }[];
  placeholder?: string;
}

export const FormControlSelect = <
  T extends string | number | "",
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TContext = unknown,
>({
  name,
  control,
  errorMsg,
  mapChangedValue,
  mapValue,
  sx,
  options,
  placeholder,
  ...selectProps
}: FormControlSelectProps<T, TFieldValues, TName, TContext>): JSX.Element => (
  <FormControl sx={sx} error={Boolean(errorMsg)} disabled={selectProps.disabled}>
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => {
        const displayValue = mapValue ? mapValue(value) : value;
        const selectValue = displayValue === undefined || displayValue === null ? "" : displayValue;
        const normalizedSelectValue =
          selectValue === "" || options.some((option) => option.value === selectValue) ? selectValue : "";

        return (
          <SelectWithError
            value={normalizedSelectValue}
            onChange={(event: SelectChangeEvent<T>) => {
              const selectedValue = event.target.value as T;
              const valueToSave =
                selectedValue === ""
                  ? (null as PathValue<TFieldValues, TName>)
                  : mapChangedValue?.(selectedValue) ?? selectedValue;
              onChange(valueToSave);
            }}
            errorMsg={errorMsg}
            options={options}
            placeholder={placeholder}
            {...selectProps}
          />
        );
      }}
    />
  </FormControl>
);
